// A board: flat dated groups, items under them, dependencies as connections.
//
// The three decisions this screen exists to hold (planning/crum/client-surface.md §4):
//
//   · Groups are FLAT. A group never contains a group. The board stays
//     spreadsheet-shaped, which is what makes it scannable — and it is literally a
//     table: one set of column tracks, shared by every group via CSS subgrid, so a
//     date in one group sits under a date in the next. Separate tables per group
//     would take the spacing and lose the alignment, which is the part worth
//     having. See .board in global.css.
//   · A dependency is a line under the row naming what it waits on — never an
//     indent, never a tree.
//   · `Blocked` is DERIVED from the edges. Nothing here writes a blocked flag,
//     because a stale one on a client's page is worse than none.
//
// The eye column is the only extra act the team performs. Flip it and the row
// appears in the client's seat, same group, same order — there is no publish step.

import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  applyOnly,
  BlockedChip,
  BoardGrid,
  BoardGroupBand,
  BoardHeaders,
  BoardRow,
  BoardSheet,
  OnlyFilter,
  OrderBy,
  type BoardColumn,
  type Only,
  type Sort,
} from '../components/BoardSheet'
import {
  ArchivedRow,
  ArchivedToggle,
  Avatar,
  Button,
  ConfirmAction,
  EditableText,
  EmptyState,
  PillSelect,
} from '../components/common'
import { Icon } from '../components/Icon'
import { Page } from '../components/Page'
import { DateCell, TextMenu } from '../components/Picker'
import { Priority, VisibilityEye } from '../components/Priority'
import { RowCard } from '../components/RowCard'
import { RowCardMark } from '../components/RowCardView'
import { newId } from '../lib/ids'
import { dueTone, ITEM_STATUSES, itemStatus, showsToClient, type Visibility } from '../lib/model'
import {
  archivedOnBoard,
  commentsFor,
  reorderItems,
  blockersOf,
  blocking,
  fieldStr,
  groupsForWork,
  itemsForGroup,
  ungroupedItems,
  itemsForWork,
  peopleForProject,
  orgsForProject,
  shownCount,
  wouldCycle,
  type EntityRecord,
  type GroupRecord,
  type ItemRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

/** Groups sit sparsely so a reorder writes one event instead of renumbering. */
function nextOrder(groups: GroupRecord[]): number {
  return groups.length === 0 ? 10 : Math.max(...groups.map((g) => g.order)) + 10
}

/**
 * The board, on its own page. A thin wrapper: the board itself is a component, so
 * a project's own record page can carry it too — which is how anyone finds it.
 * A board reachable from exactly one screen is a board nobody finds.
 */
export function BoardPage() {
  const { workId = '' } = useParams()
  const { state, version } = useStore()
  void version
  const work = state.entities[workId]
  const counts = shownCount(state, workId)

  if (!work) {
    return (
      <Page title="Not found">
        <EmptyState icon="Boxes" title="No such project" hint="It may have been archived." />
      </Page>
    )
  }

  const org = orgsForProject(state, workId)[0]

  return (
    <Page
      eyebrow="Board"
      // Clients first, the same chain every record page now shows, so the trail
      // reads the same wherever you arrived from.
      crumbs={[
        { label: 'Clients', to: '/orgs' },
        ...(org ? [{ label: fieldStr(org, 'name'), to: `/c/${org.id}` }] : []),
        { label: fieldStr(work, 'name') || 'Untitled', to: `/e/${workId}` },
      ]}
      title={fieldStr(work, 'name') || 'Untitled'}
      sub="Flat groups with their own dates. The eye shows a row on the client's page — there is nothing else to publish."
      right={
        <span className="pill" title="Rows visible on the client's page">
          ◉ {counts.shown} of {counts.total} shown to client
        </span>
      }
    >
      <Board workId={workId} />
    </Page>
  )
}

/** The board itself, without page chrome, so it can be embedded. */
export function Board({ workId }: { workId: string }) {
  const { state, version, dispatch } = useStore()
  void version
  const [adding, setAdding] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [sort, setSort] = useState<Sort>({ key: 'board', dir: 'asc' })
  // Which bucket the pointer is currently over, for the drop highlight.
  const [dropOn, setDropOn] = useState<string | null>(null)
  // And WHERE in it. A bucket-wide highlight could only ever mean "somewhere in
  // here", which is how a board ends up with an order nobody chose.
  const [dropAt, setDropAt] = useState<{ group: string; index: number } | null>(null)
  // Which row is open. On the BOARD rather than in the row, because the panel it
  // opens is a sibling of the row in the grid — a cell cannot be full width, and a
  // panel inside one would be clipped to it. One at a time: two open panels and the
  // board stops being a table you can scan.
  const [openRow, setOpenRow] = useState<string | null>(null)
  // What to look at. Defaults to everything: a board that hides what it delivered
  // is a board that cannot show a client what it delivered, and finished rows are
  // the record of the work. Narrowing is a choice you make, not one the table makes
  // for you the moment you tick something.
  const [only, setOnly] = useState<Only>('all')
  // Archiving is only half a verb without this: a row or a bucket you cannot get
  // back is one you deleted, and an append-only log deletes nothing.
  const [showArchived, setShowArchived] = useState(false)

  const groups = groupsForWork(state, workId)
  const gone = archivedOnBoard(state, workId)
  // Every item on the board, so a dependency can point at any of them, and the
  // people who could own one.
  const allItems = itemsForWork(state, workId)
  // Not every person in the log: on a real deployment that is everyone at every
  // client, so the one name you want is buried among people who could not own the
  // row. Our side plus their side. See peopleForProject.
  const people = peopleForProject(state, workId)

  const addGroup = () => {
    void dispatch({
      type: 'group.create',
      subject: newId('grp'),
      data: { work: workId, label: 'New group', order: nextOrder(groups), visibility: 'internal' },
    })
  }

  // `group` may be '': a board starts as a flat list and grows buckets later, so
  // requiring the bucket first had the order backwards.
  const addItem = (groupId: string) => {
    const label = draft.trim()
    if (!label) return
    void dispatch({
      type: 'item.create',
      subject: newId('itm'),
      data: { work: workId, group: groupId, label, status: 'next', visibility: 'internal' },
    })
    // The field stays open and focused: things arrive from a call as a list, and
    // reopening it between each one is the difference between capturing four and
    // capturing one.
    setDraft('')
  }

  const toggleVisibility = (
    rec: { id: string; visibility: Visibility },
    kind: 'group' | 'item',
  ) => {
    void dispatch({
      type: `${kind}.update`,
      subject: rec.id,
      data: { visibility: showsToClient(rec.visibility) ? 'internal' : 'client' },
    })
  }

  // Moving a row is one field write, so drag and drop is a drop target per bucket
  // rather than a sortable list: what you are changing is which bucket a row is in,
  // not where it sits within one (items have no manual order yet).
  const moveTo = (itemId: string, groupId: string) => {
    const item = state.items[itemId]
    if (!item || item.group === groupId) return
    void dispatch({ type: 'item.update', subject: itemId, data: { work: workId, group: groupId } })
  }
  /**
   * A whole group is one drop target: its heading and every row under it.
   * Aiming at the heading alone meant the rows you were looking at were not part
   * of the thing you had to hit.
   *
   * `display: contents` is what makes that possible without breaking the board —
   * the wrapper has no box of its own, so the heading and the rows stay direct
   * grid items and keep their `subgrid` columns, while drag events still bubble up
   * to it.
   */
  const dropZone = (groupId: string) => ({
    style: { display: 'contents' as const },
    className: dropOn === groupId ? 'board-drop' : undefined,
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move' as const
      setDropOn(groupId)
    },
    onDragLeave: (e: React.DragEvent) => {
      // A contents wrapper fires dragleave as the pointer crosses between its own
      // children, so only clear when the pointer has actually left the group.
      if (e.currentTarget.contains(e.relatedTarget as Node)) return
      setDropOn((cur) => (cur === groupId ? null : cur))
      setDropAt((cur) => (cur?.group === groupId ? null : cur))
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      setDropOn(null)
      setDropAt(null)
      const id = e.dataTransfer.getData('text/crum-item')
      // Dropped on the band or the empty space rather than on a row: the end of the
      // bucket, which is where "put this here" means when you did not aim at a gap.
      if (id) moveTo(id, groupId)
    },
  })

  /** Rows can only be arranged in board order. Under a lens the positions are the
   *  lens's, so a drag would be undone by the next render. */
  const manual = sort.key === 'board'

  /**
   * Put a row where it was dropped.
   *
   * Two events at most in the common case: the bucket if it changed, and the row's
   * own `order` — the midpoint between its new neighbours. `reorderItems` decides
   * whether that is enough or the bucket has to be laid out again; see reducer.ts.
   */
  const dropInto = (itemId: string, groupId: string, index: number) => {
    const item = state.items[itemId]
    if (!item) return
    if (item.group !== groupId) {
      void dispatch({
        type: 'item.update',
        subject: itemId,
        data: { work: workId, group: groupId },
      })
    }
    // The destination bucket exactly as it is drawn now — `reorderItems` works out
    // whether this is a nudge within it or an arrival from somewhere else.
    const drawn = groupId ? itemsForGroup(state, groupId) : ungroupedItems(state, workId)
    for (const patch of reorderItems(drawn, item, index)) {
      void dispatch({ type: 'item.update', subject: patch.id, data: { order: patch.order } })
    }
  }

  const visible = (list: ItemRecord[]) => applyOnly(list, only)
  const ungrouped = visible(sortItems(ungroupedItems(state, workId), sort, state))
  const rowProps = (item: ItemRecord, group: string, index: number) => ({
    state,
    siblings: allItems,
    people,
    draggable: manual,
    // The line the row would land on, drawn on the row the pointer is over: above it
    // in the top half, below it in the bottom.
    insert:
      dropAt && dropAt.group === group && (dropAt.index === index || dropAt.index === index + 1)
        ? dropAt.index === index
          ? ('before' as const)
          : ('after' as const)
        : undefined,
    onDragOver: manual
      ? (e: React.DragEvent) => {
          e.preventDefault()
          e.stopPropagation()
          const box = (e.currentTarget as HTMLElement).getBoundingClientRect()
          setDropOn(group)
          setDropAt({ group, index: e.clientY < box.top + box.height / 2 ? index : index + 1 })
        }
      : undefined,
    onDrop: manual
      ? (e: React.DragEvent) => {
          e.preventDefault()
          e.stopPropagation()
          const id = e.dataTransfer.getData('text/crum-item')
          const at = dropAt?.group === group ? dropAt.index : index
          setDropOn(null)
          setDropAt(null)
          if (id) dropInto(id, group, at)
        }
      : undefined,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData('text/crum-item', item.id)
      e.dataTransfer.effectAllowed = 'move' as const
    },
    open: openRow === item.id,
    onToggleCard: () => setOpenRow((cur) => (cur === item.id ? null : item.id)),
    onArchive: () => void dispatch({ type: 'item.archive', subject: item.id }),
    onToggle: () => toggleVisibility(item, 'item'),
    onPatch: (data: Record<string, unknown>) =>
      void dispatch({ type: 'item.update', subject: item.id, data }),
    onBlock: (on: string) => void dispatch({ type: 'item.block', subject: item.id, data: { on } }),
    onUnblock: (on: string) =>
      void dispatch({ type: 'item.unblock', subject: item.id, data: { on } }),
  })

  return (
    <>
      {/* The chrome used to be a filled button and a sentence explaining what a
          group is, at the top of every board including empty ones. Adding a row is
          the common act and it no longer needs a bucket to exist first, so that is
          the control that sits here; a group is the quieter one beside it, and what
          a group is for is obvious once you have rows to put in one. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={adding === '' ? draft : ''}
          onChange={(e) => {
            setAdding('')
            setDraft(e.target.value)
          }}
          onKeyDown={(e) => e.key === 'Enter' && addItem('')}
          placeholder="What needs doing?"
          className="rounded-control text-body min-w-0 flex-1 border px-2.5 py-1.5 outline-none sm:max-w-sm"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        />
        <button type="button" onClick={addGroup} className="meta underline">
          + group
        </button>
        <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-2">
          <OrderBy value={sort} onChange={setSort} />
          <ArchivedToggle
            count={gone.groups.length + gone.items.length}
            on={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
          />
          <OnlyFilter value={only} onChange={setOnly} />
        </span>
      </div>

      {groups.length === 0 && ungrouped.length === 0 ? (
        <EmptyState
          icon="Boxes"
          title="Nothing on this board"
          hint="Add a row above. Group them when they share a date."
        />
      ) : (
        <BoardSheet>
          {/* Outside .board on purpose — see the CSS. A sticky grid item cannot
              leave its own grid area, so a header row inside the grid never stuck,
              whatever `top` said. */}
          <BoardHeaders columns={COLUMNS} sort={sort} onSort={setSort} />
          <BoardGrid>
            {/* Ungrouped rows sit at the top, with no heading of their own when there
              are no groups to distinguish them from — a board that is just a list
              should look like just a list. The heading appears as soon as a group
              exists, because then "not in a bucket" is a real state and needs
              somewhere to drop things back to. */}
            <div {...dropZone('')}>
              {groups.length > 0 && (
                <div className="board-group board-group--none">
                  <span>Ungrouped</span>
                  <span className="meta">
                    {ungrouped.length} {ungrouped.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
              )}
              {ungrouped.map((item, i) => (
                <Fragment key={item.id}>
                  <ItemRow item={item} {...rowProps(item, '', i)} />
                  {openRow === item.id && <RowCard item={item} />}
                </Fragment>
              ))}
            </div>

            {groups.map((group) => {
              // Counts come from the whole group, not the filtered view: a heading
              // that says "3 items" because you are looking at the open ones is a
              // heading that lies about the group it names.
              const all = itemsForGroup(state, group.id)
              const items = visible(sortItems(all, sort, state))
              const shown = all.filter((i) => showsToClient(i.visibility)).length
              return (
                <div key={group.id} {...dropZone(group.id)}>
                  <BoardGroupBand
                    name={
                      <EditableText
                        value={group.label}
                        placeholder="Group name"
                        onSave={(label) =>
                          void dispatch({
                            type: 'group.update',
                            subject: group.id,
                            data: { label },
                          })
                        }
                        className="text-lead font-semibold tracking-tight"
                        // EditableText pads itself so its hover target clears the text;
                        // without pulling that back the authored group names sat 8px
                        // right of "Ungrouped", which is the same left edge.
                        style={{ marginLeft: -8 }}
                      />
                    }
                    count={`${all.length} ${all.length === 1 ? 'item' : 'items'} · ${shown} shown to client`}
                    right={
                      <>
                        {/* The date the team PROMISED. An item's date is what it
                            EXPECTS — separate on purpose, so a red group over green
                            items reads. */}
                        <DateCell
                          label="Group date"
                          value={group.due}
                          tone={group.due ? dueTone(group.due) : undefined}
                          placeholder="set a date"
                          onChange={(due) =>
                            void dispatch({
                              type: 'group.update',
                              subject: group.id,
                              data: { due },
                            })
                          }
                        />
                        <VisibilityEye
                          value={group.visibility}
                          onToggle={() => toggleVisibility(group, 'group')}
                        />
                        <Button
                          variant="quiet"
                          size="dense"
                          onClick={() => {
                            setAdding(adding === group.id ? null : group.id)
                            setDraft('')
                          }}
                        >
                          {adding === group.id ? 'Cancel' : 'Add item'}
                        </Button>
                        {/* Archiving the bucket leaves its rows on the board, under
                            Ungrouped — see archivedOnBoard — so this destroys
                            nothing and the confirm says what it does rather than
                            warning about it. It does take them off the CLIENT's
                            page, which only renders grouped rows (clientBoard):
                            the bucket was what carried them there. */}
                        <ConfirmAction
                          title="Archive this group · its rows move to Ungrouped"
                          confirmLabel="Archive group"
                          onConfirm={() =>
                            void dispatch({ type: 'group.archive', subject: group.id })
                          }
                        />
                      </>
                    }
                  />

                  {items.map((item, i) => (
                    <Fragment key={item.id}>
                      <ItemRow item={item} {...rowProps(item, group.id, i)} />
                      {openRow === item.id && <RowCard item={item} />}
                    </Fragment>
                  ))}

                  {items.length === 0 && adding !== group.id && (
                    <p className="note" style={{ gridColumn: '1 / -1', padding: '8px 0' }}>
                      Empty. Drag a row in, or add one.
                    </p>
                  )}

                  {adding === group.id && (
                    <div className="flex items-center gap-2 py-2" style={{ gridColumn: '1 / -1' }}>
                      <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem(group.id)}
                        placeholder="What needs doing?"
                        className="rounded-control text-body min-w-0 flex-1 border px-2.5 py-1.5 outline-none"
                        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
                      />
                      <Button variant="work" onClick={() => addItem(group.id)}>
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </BoardGrid>
        </BoardSheet>
      )}

      {showArchived && (gone.groups.length > 0 || gone.items.length > 0) && (
        <div className="mt-6">
          <p className="eyebrow mb-1.5">Archived</p>
          <div className="rows">
            {gone.groups.map((g) => (
              <ArchivedRow
                key={g.id}
                onRestore={() => void dispatch({ type: 'group.restore', subject: g.id })}
              >
                <Icon name="Boxes" size={12} style={{ color: 'var(--faint)' }} />{' '}
                {g.label || 'Untitled group'}
                <span className="meta"> · group</span>
              </ArchivedRow>
            ))}
            {gone.items.map((i) => (
              <ArchivedRow
                key={i.id}
                onRestore={() => void dispatch({ type: 'item.restore', subject: i.id })}
              >
                {i.label || 'Untitled row'}
              </ArchivedRow>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

/** The team's seven columns. The client's sheet declares its own six; both are
 *  rendered by `BoardHeaders` over the tracks `global.css` defines, so the header
 *  markup and the sticky behaviour cannot drift between them. */
const COLUMNS: BoardColumn[] = [
  { key: 'item', label: 'Item', sortable: true },
  { key: 'waits', label: 'Waits on', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'due', label: 'Due', sortable: true },
  { key: 'owner', label: 'Owner', sortable: true },
  // "Theirs" only read as a distinction against a team priority column, and
  // there isn't one — so it was just cryptic. The client owns this column either
  // way; the hover on each cell says whose it is.
  { key: 'theirs', label: 'Priority', sortable: true },
  // What you DO to a row, gathered at the end: shown-to-client, the note and
  // conversation card, archive. These led every line under a "◉" header, which
  // named neither what the circles did nor that they were buttons — and pushed the
  // title, the thing you actually read, half an inch in.
  { key: 'board', label: 'Shown', sortable: false },
]

/**
 * Sorting happens WITHIN a group, never across them.
 *
 * A group is a dated commitment, so its position is a decision someone made; a
 * sort is a way of reading a column. Letting a column reorder the groups would
 * quietly rewrite the promise the board is making.
 */
function sortItems(
  items: ItemRecord[],
  sort: Sort,
  state: ReturnType<typeof useStore>['state'],
): ItemRecord[] {
  // Nothing sinks. Finishing a row used to move it to the bottom of its group the
  // instant you changed the status, so the thing you had just acted on jumped away
  // from your pointer — which reads as the row having been taken from you rather
  // than ticked. What to look at is a filter you choose (see `only`), not something
  // the table decides for you the moment you touch it.
  if (sort.key === 'board') return items
  const rank = (i: ItemRecord): string => {
    switch (sort.key) {
      case 'item':
        return i.label.toLowerCase()
      case 'status':
        return blockersOf(state, i.id).length > 0 ? '0blocked' : itemStatus(i.status).label
      case 'due':
        // Undated sorts last either way: "no date" is not earlier than every date,
        // it is simply not on the scale.
        return i.due || '9999'
      case 'owner':
        return (
          state.entities[i.owner] ? fieldStr(state.entities[i.owner]!, 'name') : 'zzz'
        ).toLowerCase()
      case 'theirs':
        return String(9 - i.clientPriority)
      default:
        return ''
    }
  }
  const out = [...items].sort((a, b) => rank(a).localeCompare(rank(b)) || a.id.localeCompare(b.id))
  return sort.dir === 'asc' ? out : out.reverse()
}

function ItemRow({
  item,
  state,
  siblings,
  people,
  draggable,
  insert,
  open,
  onToggleCard,
  onDragOver,
  onDrop,
  onDragStart,
  onArchive,
  onToggle,
  onPatch,
  onBlock,
  onUnblock,
}: {
  item: ItemRecord
  state: ReturnType<typeof useStore>['state']
  /** other items on this board, for the dependency picker */
  siblings: ItemRecord[]
  people: EntityRecord[]
  /** off under a sort: a position you cannot keep is not one to offer */
  draggable: boolean
  /** draw the landing line above or below this row */
  insert?: 'before' | 'after' | undefined
  /** is this row's note and conversation open below it */
  open: boolean
  onToggleCard: () => void
  onDragOver?: ((e: React.DragEvent) => void) | undefined
  onDrop?: ((e: React.DragEvent) => void) | undefined
  onDragStart: (e: React.DragEvent) => void
  onArchive: () => void
  onToggle: () => void
  onPatch: (data: Record<string, unknown>) => void
  onBlock: (onId: string) => void
  onUnblock: (onId: string) => void
}) {
  const [picking, setPicking] = useState(false)
  const blockers = blockersOf(state, item.id)
  const blocked = blockers.length > 0
  const waiters = blocking(state, item.id)

  // Only items that would not close a loop. A board where A waits on B waits on A
  // renders two permanently blocked rows and no way to see why, so the edge is
  // refused where it is authored rather than untangled later.
  const eligible = siblings.filter(
    (s) =>
      s.id !== item.id &&
      !item.waitingOn.includes(s.id) &&
      // A finished item does not block — see blockersOf — so offering one is
      // offering a no-op that then reads as "nothing happened".
      s.status !== 'done' &&
      !wouldCycle(state, item.id, s.id),
  )

  return (
    // The whole row is the drag handle. A dedicated grip would be another 20px of
    // furniture on every line to serve an action you take rarely, and the row has
    // no competing drag behaviour of its own.
    <BoardRow
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={insert ? `board-insert-${insert}` : undefined}
    >
      <span className="min-w-0">
        <EditableText
          value={item.label}
          placeholder="What needs doing?"
          onSave={(label) => label.trim() && onPatch({ label })}
          className="text-body"
          style={{
            marginLeft: -8,
            textDecoration: item.status === 'done' ? 'line-through' : undefined,
            color: item.status === 'done' ? 'var(--faint)' : undefined,
          }}
        />
        {/* The client added this one. A flag, not a queue: the row is already in
            the log and already on their page, and this is what makes somebody
            look at it — re-file it, date it, or leave it where they put it. */}
        {item.addedBy === 'client' && (
          <span
            className="pill ml-1"
            style={{ ['--pc' as string]: 'var(--brass)' }}
            title="Added from the client's page"
          >
            client
          </span>
        )}
        {!blocked && waiters.length > 0 && (
          <span className="meta block" style={{ marginTop: -1 }}>
            {waiters.length} {waiters.length === 1 ? 'item waits' : 'items wait'} on this
          </span>
        )}
      </span>

      {/* Waits on, in a column. It was an inline line under the label that appeared
          on hover: a real field you could only find by pointing at it, and the one
          thing on the board that made two rows different heights. A blocked row
          names what it waits on; the blocker names nothing, because the reverse
          edge is derived and only the blocked side needs to render it. */}
      <span className="text-meta min-w-0">
        {picking ? (
          <TextMenu
            label="Waits on"
            value=""
            defaultOpen
            empty="Pick one…"
            options={eligible.map((cand) => ({ key: cand.id, label: cand.label }))}
            onPick={(id) => {
              if (id) onBlock(id)
              setPicking(false)
            }}
          />
        ) : blockers.length > 0 ? (
          <span className="flex min-w-0 flex-col">
            {blockers.map((bl) => (
              <span key={bl.id} className="flex min-w-0 items-center gap-1">
                <span className="truncate" style={{ color: 'var(--crit)' }} title={bl.label}>
                  {bl.label}
                </span>
                <button
                  type="button"
                  onClick={() => onUnblock(bl.id)}
                  title="No longer waiting on this"
                  aria-label="No longer waiting on this"
                  className="shrink-0"
                  style={{ color: 'var(--faint)' }}
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          </span>
        ) : eligible.length > 0 ? (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="on-hover"
            style={{ color: 'var(--faint)' }}
          >
            + add
          </button>
        ) : (
          <span style={{ color: 'var(--faint)' }}>·</span>
        )}
      </span>

      {/* A blocked row shows a label, not a control: its status is derived, so
          offering to set it would be offering something that cannot be done. It is
          the SAME pill either way now, which is the point — a column where one row
          is a system dropdown and the next is a chip does not read as a column. */}
      {blocked ? (
        <BlockedChip blockers={blockers.length} />
      ) : (
        <PillSelect
          options={ITEM_STATUSES}
          value={item.status}
          placeholder="Next"
          onPick={(status) => onPatch({ status })}
        />
      )}

      <DateCell
        label="Due"
        value={item.due}
        tone={item.due ? dueTone(item.due) : undefined}
        onChange={(due) => onPatch({ due })}
      />

      <TextMenu
        label="Owner"
        value={item.owner}
        onPick={(owner) => onPatch({ owner })}
        options={[
          { key: '', label: 'Unassigned' },
          ...people.map((per) => {
            const name = fieldStr(per, 'name') || 'Untitled'
            return {
              key: per.id,
              label: name,
              lead: <Avatar actor={{ id: per.id, name }} size={16} />,
            }
          }),
        ]}
      />

      {/* The client's own rating. They hear it on a call more often than they type
          it, so the team can record it — the column is still theirs, and the
          hover says so. */}
      <span title="The client's priority, as recorded">
        <Priority value={item.clientPriority} onSet={(v) => onPatch({ clientPriority: v })} />
      </span>

      {/* Everything you do TO the row, at the end of it. */}
      <span className="flex items-center justify-end gap-0.5">
        <RowCardMark
          hasNote={!!(item.note ?? '').trim()}
          count={commentsFor(state, item.id).length}
          open={open}
          onToggle={onToggleCard}
          note={item.note ?? ''}
          comments={commentsFor(state, item.id).map((c) => ({
            id: c.id,
            body: c.body,
            at: c.createdAt,
            by: c.createdBy?.name ?? '',
            mine: false,
          }))}
        />
        <VisibilityEye value={item.visibility} onToggle={onToggle} />
        {/* Archive stays hidden until you point at the row: it is destructive and
            rare, and a permanent × on every line is an invitation. */}
        <button
          type="button"
          onClick={onArchive}
          title="Archive this row"
          aria-label="Archive this row"
          className="on-hover -m-1 p-1"
          style={{ color: 'var(--faint)', lineHeight: 1 }}
        >
          <Icon name="X" size={12} />
        </button>
      </span>
    </BoardRow>
  )
}
